import { css } from "@microsoft/fast-element";
import { display, forcedColorsStylesheetBehavior, borderRadiusMedium, colorCompoundBrandForeground1Pressed, colorNeutralBackground1, colorNeutralBackground1Hover, colorNeutralBackground1Selected, colorNeutralBackgroundDisabled, colorNeutralForeground2, colorNeutralForeground2Hover, colorNeutralForeground2Pressed, colorNeutralForeground3, colorNeutralForegroundDisabled, colorStrokeFocus2, fontFamilyBase, fontSizeBase200, fontSizeBase300, fontWeightRegular, lineHeightBase200, lineHeightBase300, spacingHorizontalMNudge, spacingHorizontalXL, spacingHorizontalXS, spacingHorizontalXXS, strokeWidthThick } from "@fluentui/web-components";
import { sizeHorizontalXL, sizeVerticalXXXL } from "@fabric-msft/theme";
import { checkedState, disabledState, submenuState } from "../styles/states/states.js";
/** MenuItem styles
 * @public
 */
export const styles = css `
  ${display("grid")}

  :host {
    --indent: 0;
    align-items: center;
    background: ${colorNeutralBackground1};
    border-radius: ${borderRadiusMedium};
    color: ${colorNeutralForeground2};
    contain: layout;
    cursor: pointer;
    /* Prevent shrinking of MenuItems when max-height is applied to MenuList */
    flex-shrink: 0;
    font: ${fontWeightRegular} ${fontSizeBase300} / ${lineHeightBase300}
      ${fontFamilyBase};
    grid-gap: ${spacingHorizontalXS};
    grid-template-columns: ${spacingHorizontalXL} ${spacingHorizontalXL} auto ${spacingHorizontalXL};
    height: ${sizeVerticalXXXL};
    overflow: visible;
    padding: 0 ${spacingHorizontalMNudge};
  }

  :host(:hover) {
    background: ${colorNeutralBackground1Hover};
    color: ${colorNeutralForeground2Hover};
  }

  :host(:active) {
    background-color: ${colorNeutralBackground1Selected};
    color: ${colorNeutralForeground2Pressed};
  }

  :host(:active) ::slotted([slot="start"]) {
    color: ${colorCompoundBrandForeground1Pressed};
  }

  :host(${disabledState}) {
    background-color: ${colorNeutralBackgroundDisabled};
    color: ${colorNeutralForegroundDisabled};
  }

  :host(${disabledState}) ::slotted([slot="start"]),
  :host(${disabledState}) ::slotted([slot="end"]) {
    color: ${colorNeutralForegroundDisabled};
  }

  :host(:focus-visible) {
    border-radius: ${borderRadiusMedium};
    outline: ${strokeWidthThick} solid ${colorStrokeFocus2};
  }

  .content {
    white-space: nowrap; /* @design-token-ok: CSS property */
    flex-grow: 1;
    grid-column: auto / span 2;
    padding: 0 ${spacingHorizontalXXS};
  }

  :host(:not(${checkedState})) .indicator,
  :host(:not(${checkedState})) ::slotted([slot="indicator"]),
  :host(:not(${submenuState})) .submenu-glyph,
  :host(:not(${submenuState})) ::slotted([slot="submenu-glyph"]) {
    display: none;
  }

  ::slotted([slot="end"]) {
    color: ${colorNeutralForeground3};
    font: ${fontWeightRegular} ${fontSizeBase200} / ${lineHeightBase200}
      ${fontFamilyBase};
    white-space: nowrap; /* @design-token-ok: CSS property */
  }

  :host([data-indent="1"]) {
    --indent: 1;
  }

  :host([data-indent="2"]) {
    --indent: 2;
    grid-template-columns: ${spacingHorizontalXL} ${spacingHorizontalXL} auto auto;
  }

  :host(${submenuState}) {
    grid-template-columns: ${spacingHorizontalXL} auto auto ${spacingHorizontalXL};
  }

  :host([data-indent="2"]${submenuState}) {
    grid-template-columns: ${spacingHorizontalXL} ${spacingHorizontalXL} auto auto ${spacingHorizontalXL};
  }

  .indicator,
  ::slotted([slot="indicator"]) {
    grid-column: 1 / span 1;
    width: ${sizeHorizontalXL};
  }

  ::slotted([slot="start"]) {
    display: inline-flex;
    grid-column: calc(var(--indent)) / span 1;
  }

  .content {
    grid-column: calc(var(--indent) + 1) / span 1;
  }

  ::slotted([slot="end"]) {
    grid-column: calc(var(--indent) + 2) / span 1;
    justify-self: end;
  }

  .submenu-glyph,
  ::slotted([slot="submenu-glyph"]) {
    grid-column: -2 / span 1;
    justify-self: end;
  }

  @layer popover {
    :host {
      anchor-name: --menu-trigger;
      position: relative;
    }

    ::slotted([popover]) {
      margin: 0;
      max-height: var(--menu-max-height, auto);
      position: absolute;
      position-anchor: --menu-trigger;
      position-area: inline-end span-block-end;
      position-try-fallbacks: flip-inline;
      z-index: 1;
    }

    ::slotted([popover]:not(:popover-open)) {
      display: none;
    }

    ::slotted([popover]:popover-open) {
      inset: unset;
    }

    /* Fallback for no anchor-positioning */
    @supports not (anchor-name: --menu-trigger) {
      ::slotted([popover]) {
        align-self: start;
      }
    }
  }
`.withBehaviors(forcedColorsStylesheetBehavior(css `
    :host(${disabledState}),
    :host(${disabledState}) ::slotted([slot="start"]),
    :host(${disabledState}) ::slotted([slot="end"]) {
      color: GrayText;
    }
  `));
//# sourceMappingURL=menu-item.styles.js.map